<div class="vr-trk">

  <!-- A div, not a <button>: the header carries its own credits pill and the
       show-all toggle, and nesting buttons is invalid HTML. -->
  <div class="vr-trk__head"
       role="button"
       tabindex="0"
       [attr.aria-expanded]="expanded"
       (click)="toggle()"
       (keydown.enter)="toggle()"
       (keydown.space)="$event.preventDefault(); toggle()">
    <mat-icon class="material-icons-outlined vr-trk__icon">timeline</mat-icon>
    <span class="vr-trk__title">Vessel Tracks</span>
    <span class="vr-trk__count">{{ summary.tracks }}</span>
    <span class="vr-trk__spacer"></span>

    <!-- No balance pill here: refreshes and tracks now spend from ONE credit
         wallet, and its pill lives on the sensor row — rendering the same
         number twice read as two separate balances. -->

    <mat-icon class="vr-trk__caret">{{ expanded ? 'expand_less' : 'expand_more' }}</mat-icon>
  </div>

  <ng-container *ngIf="expanded">

    <!-- Nothing bought yet. Rendered rather than hidden: buying a track means
         clicking a vessel and opening a drawer on the map, which nothing else in
         the panel would ever teach. -->
    <div class="vr-trk__none" *ngIf="!summary.tracks">
      <mat-icon class="material-icons-outlined">timeline</mat-icon>
      <div>
        <div class="vr-trk__none-title">No vessel tracks yet</div>
        <div class="vr-trk__none-note">
          Click a vessel on the map, pick a trace period and press Trace. Each
          track costs {{ trackCost }} credits and is saved here, ready to replay.
        </div>
      </div>
    </div>

    <div class="vr-trk__list" *ngIf="summary.tracks">
      <ng-container *ngFor="let group of groups; trackBy: trackByMmsi">

        <!-- Vessel header. Collapsed it is one line, which is what keeps a
             dozen traced hulls from turning the sidebar into a scroll. -->
        <button type="button" class="vr-trk__grp"
                [class.is-open]="isOpen(group)"
                [attr.aria-expanded]="isOpen(group)"
                [matTooltip]="group.identity.isNamed
                  ? group.identity.label + ' · MMSI ' + group.mmsi
                  : 'This vessel broadcast no name in the AIS data'"
                matTooltipPosition="below" matTooltipClass="vr-tip-below"
                (click)="toggleGroup(group)">
          <mat-icon class="vr-trk__chev">chevron_right</mat-icon>

          <!-- No colour swatch before the name. It carried one bit — in the
               snapshot on the map, or not — which the `· not in view` note on
               the sublabel already says in words, and a 10px hue next to a name
               reads as a category the list does not have. The vessel's colour
               still marks its TRACKS below, where it matches the drawn path. -->

          <span class="vr-trk__vname" [class.is-unnamed]="!group.identity.isNamed">
            <span class="vr-trk__vlabel">{{ group.identity.label }}</span>
            <small>
              {{ group.identity.sublabel }}
              <ng-container *ngIf="group.identity.isNamed && group.identity.shipType">
                · {{ group.identity.shipType }}
              </ng-container>
              <span class="vr-trk__offmap" *ngIf="!group.onMap"> · not in view</span>
            </small>
          </span>

          <!-- A replay started from the map toolbar belongs to a group this list
               has not opened yet, so the header carries the wait until it does. -->
          <span class="vr-trk__onmap is-loading" *ngIf="groupLoading(group) && !isOpen(group)">
            <mat-icon class="vr-trk__spin">autorenew</mat-icon>LOADING
          </span>
          <span class="vr-trk__onmap"
                *ngIf="groupOnMap(group) && !groupLoading(group) && !isOpen(group)">ON MAP</span>
          <span class="vr-trk__vcount">{{ group.rows.length }}</span>
        </button>

        <ng-container *ngIf="isOpen(group)">

          <div class="vr-trk__rows" role="listbox"
               [attr.aria-label]="'Saved tracks for ' + group.identity.label">
            <div class="vr-trk__row"
                 *ngFor="let row of group.rows; trackBy: trackByTrackId"
                 role="option"
                 tabindex="0"
                 [class.is-onmap]="isOnMap(row)"
                 [class.is-loading]="isLoading(row)"
                 [class.is-pathless]="row.pathless"
                 [attr.aria-selected]="isOnMap(row)"
                 [attr.aria-busy]="isLoading(row)"
                 [attr.aria-disabled]="row.pathless"
                 [matTooltip]="row.pathless
                   ? 'Only one position was recorded in this window, so there is no path to replay — download still gives you the position'
                   : isLoading(row)
                     ? 'Recalling this track…'
                     : isOnMap(row)
                       ? 'Click to take this track off the map'
                       : 'Replay this track · fetched ' + row.ago"
                 matTooltipPosition="below" matTooltipClass="vr-tip-below"
                 [matTooltipDisabled]="childTipHover"
                 (click)="pick(row)"
                 (keydown.enter)="pick(row)"
                 (keydown.space)="$event.preventDefault(); pick(row)">
              <!-- Three states in one mark: hollow (stored), spinning arc
                   (being recalled), filled (on the map). The arc takes the
                   vessel's own colour so the wait is visibly about the path
                   that is coming, not about the panel. -->
              <span class="vr-trk__dot"
                    [class.is-loading]="isLoading(row)"
                    [style.background]="isOnMap(row) ? group.color : null"
                    [style.borderColor]="isOnMap(row) ? group.color : null">
                <i class="vr-trk__arc" *ngIf="isLoading(row)"
                   [style.borderTopColor]="group.color"></i>
              </span>
              <span class="vr-trk__span">{{ row.spanLabel }}</span>
              <span class="vr-trk__win">{{ row.window || ('fetched ' + row.ago) }}</span>

              <!-- The mouseenter/mouseleave pairs on this row's tooltip-bearing
                   children stand the row's own tooltip down while they speak —
                   see `childTipHover` on the component. -->
              <span class="vr-trk__flag is-warn" *ngIf="row.truncated"
                    matTooltip="The operator reported more positions than it returned — this track is incomplete"
                    matTooltipPosition="below" matTooltipClass="vr-tip-below"
                    (mouseenter)="childTipHover = true"
                    (mouseleave)="childTipHover = false">
                PARTIAL
              </span>
              <span class="vr-trk__flag" *ngIf="row.clamped"
                    matTooltip="The requested span exceeded the 90-day maximum and was shortened"
                    matTooltipPosition="below" matTooltipClass="vr-tip-below"
                    (mouseenter)="childTipHover = true"
                    (mouseleave)="childTipHover = false">
                CLAMPED
              </span>

              <!-- One position is a legitimate, paid answer — the vessel
                   reported once in the window — but it is not a path, and the
                   transport needs two. Said on the row so the fact is read
                   before a click, rather than after two round trips. -->
              <span class="vr-trk__flag" *ngIf="row.pathless"
                    matTooltip="Only one position was recorded in this window, so there is nothing to draw. The position itself is still in the download."
                    matTooltipPosition="below" matTooltipClass="vr-tip-below"
                    (mouseenter)="childTipHover = true"
                    (mouseleave)="childTipHover = false">
                NO PATH
              </span>

              <span class="vr-trk__pts" *ngIf="row.points != null">{{ row.points }}</span>

              <span class="vr-trk__acts"
                    (mouseenter)="childTipHover = true"
                    (mouseleave)="childTipHover = false">
                <button type="button" class="vr-trk__act is-primary"
                        [disabled]="trackLoading || row.pathless"
                        [matTooltip]="row.pathless
                          ? 'Nothing to replay — this track holds a single position'
                          : isLoading(row)
                            ? 'Recalling this track…'
                            : isOnMap(row) ? 'Take off the map' : 'Replay on the map'"
                        matTooltipPosition="below" matTooltipClass="vr-tip-below"
                        (click)="$event.stopPropagation(); pick(row)">
                  <mat-icon class="material-icons-outlined"
                            [class.vr-trk__spin]="isLoading(row)">
                    {{ isLoading(row) ? 'autorenew' : isOnMap(row) ? 'stop_circle' : 'play_arrow' }}
                  </mat-icon>
                </button>
                <button type="button" class="vr-trk__act"
                        matTooltip="Download this track"
                        matTooltipPosition="below" matTooltipClass="vr-tip-below"
                        (click)="downloadRow(row, $event)">
                  <mat-icon class="material-icons-outlined">file_download</mat-icon>
                </button>
              </span>

              <!-- Indeterminate strip along the row's own bottom edge — the same
                   shape the ON MAP bar uses for a refresh in flight. Honest by
                   construction: two opaque round trips can report no percentage,
                   so nothing here pretends to measure one. -->
              <span class="vr-trk__wire" *ngIf="isLoading(row)">
                <i [style.background]="group.color"></i>
              </span>
            </div>
          </div>

        </ng-container>

      </ng-container>
    </div>

    <!-- Stands in for the archive handoff its snapshot sibling has. Nothing is
         unreachable behind it — see `showAll` on the component. -->
    <button type="button" class="vr-trk__more" *ngIf="hasMore" (click)="toggleShowAll($event)">
      <mat-icon>{{ showAll ? 'expand_less' : 'expand_more' }}</mat-icon>
      <span class="vr-trk__more-txt">{{ moreLabel }}</span>
    </button>

  </ng-container>

</div>
